<template>
  <div class="action-buttons-container">
    <!-- Desktop/Tablet Layout -->
    <div class="action-buttons-desktop" v-if="$vuetify.breakpoint.mdAndUp">
      <v-btn
        v-if="showInsert"
        color="success"
        class="action-btn insert-btn"
        @click="$emit('insert')"
        :disabled="disabled"
        :aria-label="`${insertLabel} - Adicionar novo item`"
      >
        <v-icon left>{{ insertIcon }}</v-icon>
        {{ insertLabel }}
      </v-btn>

      <v-btn
        v-if="showEdit"
        color="grey lighten-1"
        class="action-btn edit-btn"
        @click="$emit('edit')"
        :disabled="!canEdit || disabled"
        :aria-label="`${editLabel} - Modificar item selecionado`"
      >
        <v-icon left>{{ editIcon }}</v-icon>
        {{ editLabel }}
      </v-btn>

      <v-btn
        v-if="showExport"
        color="grey lighten-1"
        class="action-btn export-btn"
        @click="$emit('export')"
        :disabled="disabled"
        :aria-label="`${exportLabel} - Exportar dados`"
      >
        <v-icon left>mdi-download</v-icon>
        {{ exportLabel }}
      </v-btn>

      <v-btn
        v-if="showActivate"
        color="grey lighten-2"
        class="action-btn activate-btn"
        @click="$emit('activate')"
        :disabled="!canActivate || disabled"
        :aria-label="`${activateLabel} - Ativar item selecionado`"
      >
        <v-icon left>mdi-power</v-icon>
        {{ activateLabel }}
      </v-btn>

      <v-btn
        v-if="showDeactivate"
        color="grey lighten-2"
        class="action-btn deactivate-btn"
        @click="$emit('deactivate')"
        :disabled="!canDeactivate || disabled"
        :aria-label="`${deactivateLabel} - Desativar item selecionado`"
      >
        <v-icon left>mdi-power-off</v-icon>
        {{ deactivateLabel }}
      </v-btn>

      <v-btn
        v-if="showDelete"
        color="error"
        class="action-btn delete-btn"
        @click="$emit('delete')"
        :disabled="!canDelete || disabled"
        :aria-label="`${deleteLabel} - Remover item selecionado`"
      >
        <v-icon left>mdi-delete</v-icon>
        {{ deleteLabel }}
      </v-btn>
    </div>

    <!-- Mobile Layout - Dropdown Menu -->
    <div class="action-buttons-mobile" v-else>
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            v-bind="attrs"
            v-on="on"
            class="mobile-menu-btn"
            :aria-label="'Menu de ações'"
          >
            <v-icon left>mdi-dots-vertical</v-icon>
            Ações
          </v-btn>
        </template>

        <v-list class="mobile-actions-list">
          <v-list-item
            v-if="showInsert"
            @click="$emit('insert')"
            :disabled="disabled"
            class="mobile-action-item insert-item"
          >
            <v-list-item-icon>
              <v-icon color="success">{{ insertIcon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ insertLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showEdit"
            @click="$emit('edit')"
            :disabled="!canEdit || disabled"
            class="mobile-action-item edit-item"
          >
            <v-list-item-icon>
              <v-icon color="grey">{{ editIcon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ editLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showExport"
            @click="$emit('export')"
            :disabled="disabled"
            class="mobile-action-item export-item"
          >
            <v-list-item-icon>
              <v-icon color="grey">mdi-download</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ exportLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showActivate"
            @click="$emit('activate')"
            :disabled="!canActivate || disabled"
            class="mobile-action-item activate-item"
          >
            <v-list-item-icon>
              <v-icon color="grey">mdi-power</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ activateLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showDeactivate"
            @click="$emit('deactivate')"
            :disabled="!canDeactivate || disabled"
            class="mobile-action-item deactivate-item"
          >
            <v-list-item-icon>
              <v-icon color="grey">mdi-power-off</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ deactivateLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showDelete"
            @click="$emit('delete')"
            :disabled="!canDelete || disabled"
            class="mobile-action-item delete-item"
          >
            <v-list-item-icon>
              <v-icon color="error">mdi-delete</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ deleteLabel }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActionButtons',
  props: {
    // Controle de visibilidade dos botões
    showInsert: { type: Boolean, default: true },
    showEdit: { type: Boolean, default: true },
    showExport: { type: Boolean, default: true },
    showActivate: { type: Boolean, default: true },
    showDeactivate: { type: Boolean, default: true },
    showDelete: { type: Boolean, default: true },
    
    // Controle de habilitação dos botões
    canEdit: { type: Boolean, default: false },
    canActivate: { type: Boolean, default: false },
    canDeactivate: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    
    // Estado geral de desabilitação
    disabled: { type: Boolean, default: false },
    
    // Labels personalizáveis
    insertLabel: { type: String, default: 'INSERIR' },
    editLabel: { type: String, default: 'EDITAR' },
    exportLabel: { type: String, default: 'EXPORTAR' },
    activateLabel: { type: String, default: 'ATIVAR' },
    deactivateLabel: { type: String, default: 'DESATIVAR' },
    deleteLabel: { type: String, default: 'EXCLUIR' },
    
    // Ícones personalizáveis
    insertIcon: { type: String, default: 'mdi-plus' },
    editIcon: { type: String, default: 'mdi-pencil' }
  },
  emits: ['insert', 'edit', 'export', 'activate', 'deactivate', 'delete']
}
</script>

<style scoped>
.action-buttons-container {
  margin: 16px 0;
}

.action-buttons-desktop {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
}

.action-btn {
  min-width: 130px;
  height: 44px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.action-btn:active {
  transform: translateY(0);
  transition: all 0.1s ease;
}

/* Botão Inserir - Verde (ação positiva) */
.insert-btn {
  background-color: #16A34A !important;
  color: white !important;
  border: none !important;
}

.insert-btn:hover {
  background-color: #15803D !important;
  box-shadow: 0 6px 20px rgba(22, 163, 74, 0.4);
}

/* Botões Neutros - Cinza neutro (Editar e Exportar) */
.edit-btn,
.export-btn {
  background-color: #9CA3AF !important;
  color: white !important;
  border: none !important;
}

.edit-btn:hover,
.export-btn:hover {
  background-color: #6B7280 !important;
  color: white !important;
  box-shadow: 0 6px 20px rgba(107, 114, 128, 0.3);
}

/* Botões Secundários - Cinza claro (Ativar e Desativar) */
.activate-btn,
.deactivate-btn {
  background-color: #D1D5DB !important;
  color: #374151 !important;
  border: none !important;
}

.activate-btn:hover,
.deactivate-btn:hover {
  background-color: #9CA3AF !important;
  color: white !important;
  box-shadow: 0 6px 20px rgba(156, 163, 175, 0.3);
}

/* Botão Excluir - Vermelho (ação crítica) */
.delete-btn {
  background-color: #EF4444 !important;
  color: white !important;
  border: none !important;
}

.delete-btn:hover {
  background-color: #DC2626 !important;
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

/* Botões Desabilitados */
.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
  background-color: #F3F4F6 !important;
  color: #9CA3AF !important;
  border-color: #E5E7EB !important;
}

/* Layout Mobile */
.action-buttons-mobile {
  display: flex;
  justify-content: center;
}

.mobile-menu-btn {
  min-width: 130px;
  height: 44px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 10px;
  background-color: #16A34A !important;
  color: white !important;
}

.mobile-menu-btn:hover {
  background-color: #15803D !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
}

.mobile-actions-list {
  min-width: 220px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 1px solid #E5E7EB;
}

.mobile-action-item {
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #F3F4F6;
}

.mobile-action-item:last-child {
  border-bottom: none;
}

.mobile-action-item:hover:not(.v-list-item--disabled) {
  background-color: #F9FAFB;
  transform: translateX(4px);
}

.mobile-action-item.v-list-item--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background-color: #F9FAFB;
}

/* Estilos específicos para mobile */
.insert-item:hover:not(.v-list-item--disabled) {
  background-color: #F0FDF4;
  border-left: 4px solid #16A34A;
}

.edit-item:hover:not(.v-list-item--disabled),
.export-item:hover:not(.v-list-item--disabled) {
  background-color: #F9FAFB;
  border-left: 4px solid #6B7280;
}

.activate-item:hover:not(.v-list-item--disabled),
.deactivate-item:hover:not(.v-list-item--disabled) {
  background-color: #F3F4F6;
  border-left: 4px solid #9CA3AF;
}

.delete-item:hover:not(.v-list-item--disabled) {
  background-color: #FEF2F2;
  border-left: 4px solid #EF4444;
}

/* Responsividade */
@media (max-width: 1200px) {
  .action-buttons-desktop {
    gap: 14px;
  }
  
  .action-btn {
    min-width: 120px;
    height: 42px;
  }
}

@media (max-width: 960px) {
  .action-buttons-desktop {
    gap: 12px;
    justify-content: center;
  }
  
  .action-btn {
    min-width: 110px;
    height: 40px;
    font-size: 0.8rem;
  }
}

@media (max-width: 600px) {
  .action-buttons-desktop {
    gap: 10px;
    flex-direction: column;
    align-items: stretch;
  }
  
  .action-btn {
    min-width: 100%;
    height: 44px;
    font-size: 0.875rem;
  }
  
  .mobile-menu-btn {
    min-width: 140px;
    height: 48px;
  }
}

/* Melhorias de acessibilidade */
.action-btn:focus {
  outline: 2px solid #3B82F6;
  outline-offset: 2px;
}

.mobile-action-item:focus {
  outline: 2px solid #3B82F6;
  outline-offset: -2px;
}

/* Animações suaves */
.action-btn,
.mobile-action-item {
  will-change: transform, box-shadow;
}

/* Estados de loading (opcional) */
.action-btn.loading {
  pointer-events: none;
  opacity: 0.7;
}

.action-btn.loading .v-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

